import { useState, type FormEvent } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import { useAuth } from "../context/AuthContext";

export function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const { signIn, signInWithGoogle, signInWithMicrosoft } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const from = (location.state as { from?: string })?.from ?? "/events";

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);

    try {
      await signIn(email, password);
      navigate(from, { replace: true });
    } catch {
      setError("Invalid email or password.");
    } finally {
      setSubmitting(false);
    }
  }

  async function handleGoogleSignIn() {
    setError(null);
    setSubmitting(true);

    try {
      await signInWithGoogle();
      navigate(from, { replace: true });
    } catch {
      setError("Google sign-in failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  async function handleMicrosoftSignIn() {
    setError(null);
    setSubmitting(true);

    try {
      await signInWithMicrosoft();
      navigate(from, { replace: true });
    } catch (err: unknown) {
      const firebaseError = err as { code?: string; message?: string; customData?: { email?: string } };
      console.error('[Microsoft Auth]', firebaseError.code, firebaseError.message, firebaseError.customData);
      setError(`Microsoft sign-in failed: ${firebaseError.code ?? 'unknown error'}`);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Button
        data-testid="microsoft-signin-button"
        variant="contained"
        fullWidth
        size="large"
        disabled={submitting}
        onClick={handleMicrosoftSignIn}
        sx={{ py: 1.5, mb: 2 }}
      >
        Sign in with Microsoft
      </Button>

      <Button
        data-testid="google-signin-button"
        variant="contained"
        fullWidth
        size="large"
        disabled={submitting}
        onClick={handleGoogleSignIn}
        sx={{ py: 1.5 }}
      >
        Sign in with Google
      </Button>

      <Divider sx={{ my: 3 }}>or</Divider>

      <TextField
        label="Email"
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        fullWidth
        required
        sx={{ mb: 2 }}
      />
      <TextField
        label="Password"
        type="password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        fullWidth
        required
        sx={{ mb: 3 }}
      />
      <Button
        data-testid="email-signin-button"
        type="submit"
        variant="contained"
        fullWidth
        size="large"
        disabled={submitting}
      >
        {submitting ? "Signing in..." : "Sign In"}
      </Button>
    </Box>
  );
}
